import DatePicker from "@rio-cloud/rio-uikit/DatePicker";
import moment from "moment";

import InputSelect from "@components/Dialog/Receipt/select";
import { errorClass, renderErrorFeedback } from "@components/shared/formFeedback";
import {
  buildRecurrenceEndYearOptions,
  buildYearOptions,
  isRecurrenceEndDateAllowed,
} from "@utils/transactionRecurrence";

import type { SelectOption } from "@rio-cloud/rio-uikit";
import type { NameInputChange } from "@components/Dialog/Receipt/form";

type TranslateFn = (id: string, values?: Record<string, string | number>) => string;

export type RecurrenceWeekDayOption = { id: string; label: string };

const WEEK_DAY_IDS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export const buildRepeatEveryOptions = (t: TranslateFn, prefix: string): SelectOption[] => [
  { id: "dia", label: t(`${prefix}.form.repeatEvery.day`) },
  { id: "semana", label: t(`${prefix}.form.repeatEvery.week`) },
  { id: "mes", label: t(`${prefix}.form.repeatEvery.month`) },
  { id: "ano", label: t(`${prefix}.form.repeatEvery.year`) },
];

export const buildWeekDayOptions = (t: TranslateFn, prefix: string): RecurrenceWeekDayOption[] =>
  WEEK_DAY_IDS.map((id) => ({ id, label: t(`${prefix}.form.weekday.${id}`) }));

type RecurrenceDateFieldProps = {
  id: string;
  label: string;
  placeholder: string;
  momentLocale: string;
  value?: string;
  errorMessage?: string;
  isValidDate?: (current: moment.Moment) => boolean;
  onChange: (value: string) => void;
};

const RecurrenceDateField = ({
  id,
  label,
  placeholder,
  momentLocale,
  value,
  errorMessage,
  isValidDate,
  onChange,
}: RecurrenceDateFieldProps) => (
  <div className={`flex-1-1 form-group width-100pct-xs ${errorClass(errorMessage)}`} style={{ minWidth: 0 }}>
    <label className="margin-0 margin-bottom-5" htmlFor={id}>
      {label}
    </label>
    <DatePicker
      id={id}
      dateFormat="DD/MM/YYYY"
      locale={momentLocale}
      onChange={(val) => {
        const formatted = typeof val === "string" ? val : val.format("YYYY-MM-DD");
        onChange(formatted);
      }}
      value={value ? moment(value).format("DD/MM/YYYY") : ""}
      className="margin-0"
      timeFormat={false}
      inputProps={{ placeholder }}
      isValidDate={isValidDate}
    />
    {renderErrorFeedback(errorMessage)}
  </div>
);

export type RecurrenceLabels = {
  repeatEvery: string;
  weekDays: string;
  recurrenceStartYear: string;
  recurrenceEndYear: string;
  recurrenceStart: string;
  recurrenceEnd: string;
  datePlaceholder: string;
};

type RecurrenceFieldsProps = {
  className: string;
  labels: RecurrenceLabels;
  repeatEveryOptions: SelectOption[];
  weekDayOptions: RecurrenceWeekDayOption[];
  momentLocale: string;
  repeatEvery?: string;
  weekDays?: string[];
  recurrenceStart?: string;
  recurrenceEnd?: string;
  recurrenceEndError?: string;
  dueDate?: string;
  inputChange: (name: NameInputChange, value?: string) => void;
  onWeekDaysChange: (weekDays: string[]) => void;
};

const RecurrenceFields = ({
  className,
  labels,
  repeatEveryOptions,
  weekDayOptions,
  momentLocale,
  repeatEvery,
  weekDays,
  recurrenceStart,
  recurrenceEnd,
  recurrenceEndError,
  dueDate,
  inputChange,
  onWeekDaysChange,
}: RecurrenceFieldsProps) => (
  <div className={className}>
    <div className="flex-1-1 width-100pct-xs" style={{ minWidth: 0 }}>
      <InputSelect
        inputChange={inputChange}
        label={labels.repeatEvery}
        name="repeatEvery"
        onCreateNewOption={() => {}}
        options={repeatEveryOptions}
        value={repeatEvery ?? ""}
        useFilter={false}
      />
    </div>

    {repeatEvery === "semana" && (
      <div className="form-group width-100pct-xs" style={{ flex: "0 0 auto" }}>
        <label className="margin-0 margin-bottom-5">{labels.weekDays}</label>
        <div className="display-flex gap-3">
          {weekDayOptions.map((day) => {
            const selected = !!weekDays?.includes(day.id);
            return (
              <button
                key={day.id}
                type="button"
                onClick={() => {
                  const current = weekDays ?? [];
                  const next = selected ? current.filter((d) => d !== day.id) : [...current, day.id];
                  onWeekDaysChange(next);
                }}
                className={`btn btn-icon-only rounded-small text-size-12 ${selected ? "btn-primary" : "btn-muted"}`}
                style={{ width: 26, height: 26, padding: 0, flex: "0 0 auto" }}
              >
                {day.label}
              </button>
            );
          })}
        </div>
      </div>
    )}

    {repeatEvery === "ano" ? (
      <>
        <div className="flex-1-1 width-100pct-xs" style={{ minWidth: 0 }}>
          <InputSelect
            inputChange={inputChange}
            label={labels.recurrenceStartYear}
            name="recurrenceStart"
            onCreateNewOption={() => {}}
            options={buildYearOptions(recurrenceStart, dueDate)}
            value={recurrenceStart ?? ""}
            useFilter={false}
          />
        </div>
        <div className="flex-1-1 width-100pct-xs" style={{ minWidth: 0 }}>
          <InputSelect
            inputChange={inputChange}
            label={labels.recurrenceEndYear}
            name="recurrenceEnd"
            onCreateNewOption={() => {}}
            options={buildRecurrenceEndYearOptions(recurrenceStart || dueDate, dueDate)}
            value={recurrenceEnd ?? ""}
            useFilter={false}
            useClear
          />
        </div>
      </>
    ) : (
      <>
        <RecurrenceDateField
          id="inputRecurrenceStart"
          label={labels.recurrenceStart}
          placeholder={labels.datePlaceholder}
          momentLocale={momentLocale}
          value={recurrenceStart}
          onChange={(value) => inputChange("recurrenceStart", value)}
        />
        <RecurrenceDateField
          id="inputRecurrenceEnd"
          label={labels.recurrenceEnd}
          placeholder={labels.datePlaceholder}
          momentLocale={momentLocale}
          value={recurrenceEnd}
          errorMessage={recurrenceEndError}
          isValidDate={(current) => isRecurrenceEndDateAllowed(current, recurrenceStart || dueDate)}
          onChange={(value) => inputChange("recurrenceEnd", value)}
        />
      </>
    )}
  </div>
);

export default RecurrenceFields;
